"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import { Search } from "lucide-react"
import { cn } from "@/lib/utils"

export type Link2 = { label: string; url: string }

export function OfficialLinks({ links }: { links: Link2[] }) {
  if (!links.length) return null
  return (
    <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
      {links.map((l) => (
        <li key={l.url}><a className="underline" href={l.url} target="_blank" rel="noopener nofollow">{l.label}</a></li>
      ))}
    </ul>
  )
}

export function NeedsCheck({ show }: { show: boolean }) {
  if (!show) return null
  return <p className="mt-2 text-xs text-muted-foreground">Some details couldn&apos;t be verified. Confirm on the official page.</p>
}

export function Contact({ phone, email }: { phone?: string | null; email?: string | null }) {
  const isEmail = !!email && /^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i.test(email)
  if (!phone && !email) return null
  return (
    <p className="mt-2 text-sm">
      {phone && <span>Phone: {/\d{3}/.test(phone) && /^[\d\s().+-]+$/.test(phone) ? <a className="underline" href={`tel:${phone.replace(/[^\d+]/g, "")}`}>{phone}</a> : phone}</span>}
      {phone && email && <span className="text-muted-foreground"> · </span>}
      {email && <span>Email: {isEmail ? <a className="underline" href={`mailto:${email}`}>{email}</a> : email}</span>}
    </p>
  )
}

export function SearchBox({ value, onChange, placeholder, count, total }: { value: string; onChange: (v: string) => void; placeholder: string; count: number; total: number }) {
  return (
    <div>
      <label className="flex items-center gap-2 rounded-lg border border-border bg-background p-3">
        <Search className="h-5 w-5 text-muted-foreground" aria-hidden />
        <span className="sr-only">Search</span>
        <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full bg-transparent text-base outline-none" autoComplete="off" />
      </label>
      <p className="mt-2 text-sm text-muted-foreground" aria-live="polite">{count} of {total}{value ? ` match “${value}”` : ""}</p>
    </div>
  )
}

export function Chips<T extends string>({ options, value, onChange, label }: { options: { id: T; label: string }[]; value: T; onChange: (v: T) => void; label: string }) {
  return (
    <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.id} aria-pressed={value === o.id} onClick={() => onChange(o.id)}
          className={cn("rounded-full border px-3 py-1 text-sm", value === o.id ? "border-accent bg-accent/15 text-foreground" : "border-border text-muted-foreground hover:text-foreground")}>
          {o.label}
        </button>
      ))}
    </div>
  )
}

/** Highlights and scrolls to the card named in the URL hash, e.g. /directory/report#fbi-ic3. */
export function useHashTarget(override?: string): string | null {
  const [id, setId] = useState<string | null>(null)
  useEffect(() => {
    if (override) { setId(override); return }
    const read = () => setId(window.location.hash.replace("#", "") || null)
    read()
    window.addEventListener("hashchange", read)
    return () => window.removeEventListener("hashchange", read)
  }, [override])
  useEffect(() => { if (id) document.getElementById(id)?.scrollIntoView({ block: "start" }) }, [id])
  return id
}

export function Disclosure({ verifiedOn, subject = "organization" }: { verifiedOn: string; subject?: string }) {
  return (
    <div className="mt-10 rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
      <p>
        <strong className="text-foreground">We have no affiliation or commercial relationship with any {subject} listed here.</strong> A listing is not an endorsement or a ranking.
        Contact details come from each organization&apos;s own website (checked {verifiedOn}) and may change. We list a phone number or email only when we read it on an official page.
        Spot an error? <Link href="/contact" className="underline">Tell us</Link>. General information only, not legal advice: see the <Link href="/disclaimer" className="underline">disclaimer</Link>.
      </p>
    </div>
  )
}

export function MoreResources({ exclude }: { exclude?: string }) {
  return (
    <p className="mt-6 text-sm text-muted-foreground">
      More resources: <Link href="/directory" className="underline">all directories</Link>
      {exclude !== "report" && <> · <Link href="/directory/report" className="underline">Who to report to</Link></>}
      {exclude !== "platforms" && <> · <Link href="/directory/platforms" className="underline">Platform contacts</Link></>}
      {exclude !== "ir" && <> · <Link href="/directory/ir-guidance" className="underline">Choosing an incident-response firm</Link></>}
      {exclude !== "individuals" && <> · <Link href="/directory/individuals" className="underline">For affected individuals</Link></>}
    </p>
  )
}
